import { BillingPlanResponse } from "types/billing/BillingPlanResponse";
import { Plan as PlanResponse } from "types/billingPlans/Plan";
import { PlansResponse } from "./models/PlansResponse";
import { ProductOptionStatus, PurchaseStatus } from "./models/PurchaseStatus";
import { UserPaymentsResponse } from "./models/UserPaymentsResponse";
import { UserPurchasesResponse, AdminPurchasesResponse } from "./models/UserPurchasesResponse";
import { UserInvoicesResponse } from "./models/UserInvoicesResponse";
import type { UserPaymentsResponse as UserPaymentsResponseType } from "types/billing/UserPaymentsResponse";
import type {
  UserPurchasesResponse as UserPurchasesResponseType,
  AdminPurchasesResponse as AdminPurchasesResponseType,
} from "types/billing/UserPurchasesResponse";
import type { UserInvoicesResponse as UserInvoicesResponseType } from "types/billing/UserInvoicesResponse";
import { prependProxyBaseUrl, request } from "api/request";
import type { BillingPlanType } from "types/billing/BillingPlan";
import type { CreatePlanRequest } from "./models/CreatePlanRequest";
import type { UpdatePlanRequest } from "./models/UpdatePlanRequest";

const PROXY_PREFIX = prependProxyBaseUrl("/api/billing-plan");
const PLANS_ROUTE = `${PROXY_PREFIX}/plans`;
const USER_BILLING_ENDPOINT_PATH = (userId: string) => `${PROXY_PREFIX}/plans/users/${userId}`;
const USER_PURCHASE_DEAL_ENDPOINT_PATH = (userId: string, dealId: string) => `${PROXY_PREFIX}/users/${userId}/purchases/${dealId}`;
const USER_PURCHASES_ENDPOINT_PATH = (userId: string) => `${PROXY_PREFIX}/users/${userId}/purchases`;
const USER_INVOICES_ENDPOINT_PATH = (userId: string) => `${PROXY_PREFIX}/users/${userId}/invoices`;
const USER_PAYMENTS_ENDPOINT_PATH = (userId: string) => `${PROXY_PREFIX}/users/${userId}/payment-profiles`;
const ADMIN_PURCHASES_ENDPOINT_PATH = `${PROXY_PREFIX}/admin/purchases`;

export async function getBillingStatus(userId: string): Promise<BillingPlanType | undefined> {
  try {
    return await request()
      .url(`${USER_BILLING_ENDPOINT_PATH(userId)}`)
      .get()
      .json((json) => {
        if (BillingPlanResponse.guard(json)) {
          return json.plan;
        } else {
          return undefined;
        }
      });
  } catch (error) {
    return undefined;
  }
}

export async function getBillingPlans(): Promise<PlansResponse> {
  const maybePlans = await request()
    .url(`${PLANS_ROUTE}?${new URLSearchParams({ fetchPlanUsers: "true" })}`)
    .get()
    .json();
  const areValidPlans = PlansResponse.validate(maybePlans);

  if (!areValidPlans.success) {
    console.error(areValidPlans);
    throw new Error(areValidPlans.message);
  }

  return areValidPlans.value;
}

export async function createBillingPlan(payload: CreatePlanRequest): Promise<PlanResponse> {
  const maybeNewPlan = await request().url(PLANS_ROUTE).post(payload).json();
  const isValidPlan = PlanResponse.validate(maybeNewPlan);

  if (!isValidPlan.success) {
    console.error(isValidPlan);
    throw new Error(isValidPlan.message);
  }

  return isValidPlan.value;
}

export async function updateBillingPlan(code: string, payload: UpdatePlanRequest): Promise<PlanResponse> {
  const response = await request().url(`${PLANS_ROUTE}/${code}`).put(payload).json();
  const isValidPlan = PlanResponse.validate(response);

  if (!isValidPlan.success) {
    console.error(isValidPlan);
    throw new Error(isValidPlan.message);
  }

  return isValidPlan.value;
}

/**
 * Get the purchase status for a specific deal
 * @param userId The ID of the user
 * @param dealId The ID of the deal to check purchase status for
 * @returns A record of product options and their purchase status
 */
export async function getPurchaseStatus(userId: string, dealId: string): Promise<PurchaseStatus> {
  const response = await request().url(USER_PURCHASE_DEAL_ENDPOINT_PATH(userId, dealId)).get().json();

  const isValidPurchaseStatus = PurchaseStatus.validate(response);

  if (!isValidPurchaseStatus.success) {
    console.error(isValidPurchaseStatus);
    throw new Error(isValidPurchaseStatus.message);
  }

  return isValidPurchaseStatus.value;
}

/**
 * Purchase a specific deal option
 * @param userId The ID of the user
 * @param dealId The ID of the deal to purchase
 * @param purchaseKey The key of the purchase option (e.g., "scorecard", "investment")
 * @param creditCardDetails Optional credit card details for the purchase
 * @returns The updated purchase status for the deal
 */
export async function purchaseDeal(
  userId: string,
  dealId: string,
  purchaseKey: string,
  creditCardDetails?: {
    token?: string;
    gatewayHandle?: string;
  }
): Promise<ProductOptionStatus> {
  const response = await request()
    .url(`${PROXY_PREFIX}/users/${userId}/purchases/${dealId}/${purchaseKey}`)
    .put({
      creditCardDetails,
    })
    .json();

  const isValidProductOptionStatus = ProductOptionStatus.validate(response);

  if (!isValidProductOptionStatus.success) {
    console.error(isValidProductOptionStatus);
    throw new Error(isValidProductOptionStatus.message);
  }

  return isValidProductOptionStatus.value;
}

/**
 * Get all payments for a user
 * @param userId The ID of the user
 * @returns A list of all payments made by the user
 */
export async function getUserPayments(userId: string): Promise<UserPaymentsResponseType> {
  const response = await request().url(USER_PAYMENTS_ENDPOINT_PATH(userId)).get().json();
  const isValidPaymentsResponse = UserPaymentsResponse.validate(response);

  if (!isValidPaymentsResponse.success) {
    console.error(isValidPaymentsResponse);
    throw new Error(isValidPaymentsResponse.message);
  }

  return isValidPaymentsResponse.value;
}

/**
 * Get all purchases for a user
 * @param userId The ID of the user
 * @returns A list of all purchases made by the user
 */
export async function getUserPurchases(userId: string): Promise<UserPurchasesResponseType> {
  const response = await request().url(USER_PURCHASES_ENDPOINT_PATH(userId)).get().json();
  const isValidPurchasesResponse = UserPurchasesResponse.validate(response);

  if (!isValidPurchasesResponse.success) {
    console.error(isValidPurchasesResponse);
    throw new Error(isValidPurchasesResponse.message);
  }

  return isValidPurchasesResponse.value;
}

/**
 * Get all invoices for a user
 * @param userId The ID of the user
 * @returns A list of all invoices made by the user
 */
export async function getUserInvoices(userId: string): Promise<UserInvoicesResponseType> {
  const response = await request().url(USER_INVOICES_ENDPOINT_PATH(userId)).get().json();
  const isValidInvoicesResponse = UserInvoicesResponse.validate(response);

  if (!isValidInvoicesResponse.success) {
    console.error(isValidInvoicesResponse);
    throw new Error(isValidInvoicesResponse.message);
  }

  return isValidInvoicesResponse.value;
}

/**
 * Get all purchases across the system (admin endpoint)
 * @returns A list of all purchases made by all users
 */
export async function getAdminPurchases(): Promise<AdminPurchasesResponseType> {
  const response = await request().url(ADMIN_PURCHASES_ENDPOINT_PATH).get().json();
  const isValidPurchasesResponse = AdminPurchasesResponse.validate(response);

  if (!isValidPurchasesResponse.success) {
    console.error(isValidPurchasesResponse);
    throw new Error(isValidPurchasesResponse.message);
  }

  return isValidPurchasesResponse.value;
}
